import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { addMilestone, updateMilestone } from '../../store/slices/contractsSlice';
import type { AppDispatch } from '../../store';
import type { Contract, ContractMilestone, MilestoneStatus } from '../../types/contracts';
import { MILESTONE_STATUSES } from '../../types/contracts';
import contractService from '../../services/contractService';
import DatePicker from '../forms/DatePicker';

interface ContractMilestonesProps {
  contract: Contract;
}

const ContractMilestones: React.FC<ContractMilestonesProps> = ({ contract }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<ContractMilestone | null>(null);
  const [formData, setFormData] = useState<Partial<ContractMilestone>>({
    title: '',
    description: '',
    dueDate: '',
    amount: 0,
    currency: contract.currency,
    status: 'pending',
  });

  const handleAddMilestone = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await dispatch(
        addMilestone({
          contractId: contract.id,
          milestone: {
            ...formData,
            dueDate: new Date(formData.dueDate!),
          } as Omit<ContractMilestone, '_id' | 'createdAt' | 'updatedAt'>,
        })
      ).unwrap();

      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to add milestone:', error);
    }
  };

  const handleUpdateMilestone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMilestone) return;

    try {
      await dispatch(
        updateMilestone({
          contractId: contract.id,
          milestoneId: editingMilestone._id || editingMilestone.id!,
          data: {
            ...formData,
            dueDate: new Date(formData.dueDate!),
          },
        })
      ).unwrap();

      setEditingMilestone(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update milestone:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      dueDate: '',
      amount: 0,
      currency: contract.currency,
      status: 'pending',
    });
  };

  const startEditing = (milestone: ContractMilestone) => {
    setEditingMilestone(milestone);
    setFormData({
      title: milestone.title,
      description: milestone.description,
      dueDate: new Date(milestone.dueDate).toISOString().split('T')[0],
      amount: milestone.amount,
      currency: milestone.currency,
      status: milestone.status,
      remarks: milestone.remarks,
    });
  };

  const cancelEditing = () => {
    setEditingMilestone(null);
    setShowAddModal(false);
    resetForm();
  };

  const getMilestoneStatusColor = (status: MilestoneStatus) => {
    const statusConfig = MILESTONE_STATUSES.find(s => s.value === status);
    return statusConfig?.color || 'gray';
  };

  const isOverdue = (milestone: ContractMilestone) => {
    return (
      milestone.status !== 'completed' &&
      milestone.status !== 'cancelled' &&
      new Date(milestone.dueDate) < new Date()
    );
  };

  const sortedMilestones = [...contract.milestones].sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Contract Milestones</h3>
          <p className="text-sm text-gray-600 mt-1">Track progress and manage milestone payments</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Milestone
        </button>
      </div>

      {/* Progress Summary */}
      {contract.milestones.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">Overall Progress</div>
              <div className="text-2xl font-semibold text-gray-900">
                {contractService.calculateProgress(contract.milestones)}%
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Value Completed</div>
              <div className="text-lg font-medium text-gray-900">
                {contractService.formatCurrency(
                  contract.completedMilestoneAmount || 0,
                  contract.currency
                )}{' '}
                /{' '}
                {contractService.formatCurrency(
                  contract.totalMilestoneAmount || 0,
                  contract.currency
                )}
              </div>
            </div>
          </div>
          <div className="mt-3">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${contractService.calculateProgress(contract.milestones)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Milestones List */}
      {sortedMilestones.length === 0 ? (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No milestones yet</h3>
          <p className="text-gray-500 mb-4">
            Add milestones to track contract progress and payments
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark"
          >
            Add First Milestone
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedMilestones.map((milestone, index) => (
            <div
              key={milestone._id || milestone.id || index}
              className={`bg-white border rounded-lg p-4 ${
                isOverdue(milestone) ? 'border-red-200 bg-red-50' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-lg font-medium text-gray-900">{milestone.title}</h4>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${getMilestoneStatusColor(milestone.status)}-100 text-${getMilestoneStatusColor(milestone.status)}-800`}
                    >
                      {MILESTONE_STATUSES.find(s => s.value === milestone.status)?.label}
                    </span>
                    {isOverdue(milestone) && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Overdue
                      </span>
                    )}
                  </div>

                  {milestone.description && (
                    <p className="text-gray-600 mt-2">{milestone.description}</p>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Due Date
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {new Date(milestone.dueDate).toLocaleDateString()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Amount
                      </dt>
                      <dd className="mt-1 text-sm font-medium text-gray-900">
                        {contractService.formatCurrency(milestone.amount, milestone.currency)}
                      </dd>
                    </div>
                    {milestone.completedDate && (
                      <div>
                        <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Completed
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(milestone.completedDate).toLocaleDateString()}
                        </dd>
                      </div>
                    )}
                  </div>

                  {milestone.remarks && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">{milestone.remarks}</p>
                    </div>
                  )}
                </div>

                <div className="ml-4">
                  <button
                    onClick={() => startEditing(milestone)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Milestone Modal */}
      {(showAddModal || editingMilestone) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <form onSubmit={editingMilestone ? handleUpdateMilestone : handleAddMilestone}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
                </h3>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Milestone Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title || ''}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <DatePicker
                      label="Due Date *"
                      value={formData.dueDate || null}
                      onChange={(date) => setFormData(prev => ({ ...prev, dueDate: date || '' }))}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={formData.status || 'pending'}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          status: e.target.value as MilestoneStatus,
                        }))
                      }
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    >
                      {MILESTONE_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                  <div className="flex">
                    <input
                      type="number"
                      step="0.01"
                      value={formData.amount || ''}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) }))
                      }
                      className="block w-full px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                      required
                    />
                    <select
                      value={formData.currency || contract.currency}
                      onChange={e => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                      className="border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>

                {formData.status === 'completed' && (
                  <div>
                    <DatePicker
                      label="Completion Date"
                      value={
                        formData.completedDate
                          ? new Date(formData.completedDate).toISOString().split('T')[0]
                          : null
                      }
                      onChange={(date) =>
                        setFormData(prev => ({ ...prev, completedDate: date || '' }))
                      }
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Remarks</label>
                  <textarea
                    value={formData.remarks || ''}
                    onChange={e => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    rows={2}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    placeholder="Optional notes about this milestone..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-orange text-white rounded-lg text-sm font-medium hover:bg-brand-orange-dark"
                >
                  {editingMilestone ? 'Update Milestone' : 'Add Milestone'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractMilestones;
